/**
 * Paying an organization (docs/plans/funds-platform.md §4).
 *
 * Nothing here can move money on its own — it decides whether a payout is *permitted* and what
 * entries it would produce. The service layer owns the row lock and the write. That split matters
 * because the single most dangerous bug available in this file is a balance check that is correct
 * in isolation and raced in practice: two browser tabs, one balance, two payouts. The check lives
 * here; the `SELECT … FOR UPDATE` that makes it true lives in `payout-service.ts`, and neither is
 * sufficient alone.
 */
import { DomainError } from "./errors.js";
import { sanctionsListIsCurrent } from "./sanctions.js";
import { assertBalanced } from "./ledger.js";
import { isNegative, isZero } from "./money.js";
import { StateMachine } from "./state-machine.js";
export const PAYOUT_STATES = [
    "requested",
    "approved",
    "rejected",
    "submitted",
    "settled",
    "failed",
    "returned",
];
/**
 * ```
 * requested → approved → submitted → settled
 *       ↓          ↓          ↓
 *    rejected   failed    returned
 * ```
 *
 * `settled → returned` is the transition people forget, and it is the expensive one: an ACH credit
 * can come back days after it looked final. A machine whose terminal state is `settled` quietly
 * makes returns unrepresentable, and the money is gone either way.
 */
export const payoutStateMachine = new StateMachine({
    name: "payout",
    initial: "requested",
    transitions: {
        requested: ["approved", "rejected"],
        approved: ["submitted", "rejected", "failed"],
        // `failed` from submitted is a same-day refusal at the rail; `returned` is a later ACH return.
        submitted: ["settled", "failed", "returned"],
        settled: ["returned"],
        rejected: [],
        failed: [],
        returned: [],
    },
    progression: ["requested", "approved", "submitted", "settled"],
    terminal: ["rejected", "failed", "returned"],
});
/** How long after a gift confirms before it is safe to pay out (ACH return window, §3.4). */
export const ACH_RETURN_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;
/**
 * How long a newly added or changed bank account is not payable to.
 *
 * The fraud this defeats: compromise one organization administrator, repoint the destination
 * account, wait for the next payout. Every other control on that change — step-up, a second
 * approver, notification to every administrator — depends on somebody *reading a message in time*.
 * The cooling-off period is the one control that works while everyone is asleep.
 */
export const PAYOUT_COOLING_OFF_MS = 3 * 24 * 60 * 60 * 1000;
/** Above this, a platform reviewer approves rather than an organization administrator (§4.4). */
export const PAYOUT_REVIEW_THRESHOLD_MINOR = 250000n;
/** Rolling 24-hour ceiling per organization, above which everything queues for review. */
export const PAYOUT_ROLLING_WINDOW_MS = 24 * 60 * 60 * 1000;
export const PAYOUT_ROLLING_LIMIT_MINOR = 1000000n;
/** A new organization cannot be paid until it has been on the platform this long (§4.4). */
export const NEW_ORGANIZATION_HOLD_MS = 14 * 24 * 60 * 60 * 1000;
/* ------------------------------------------------------------- the refusals */
/**
 * Every reason a payout can be refused, as a value rather than a message.
 *
 * Named separately because the plan requires three *different* refusals with three *different*
 * messages — an organization told "payouts are unavailable" learns nothing, and the support
 * conversation that follows costs more than the enum did.
 */
export const PAYOUT_REFUSALS = [
    "feature_disabled",
    "organization_not_approved",
    "no_verified_bank_account",
    "bank_account_cooling_off",
    "sanctions_not_cleared",
    "sanctions_list_stale",
    "payouts_halted",
    "organization_too_new",
    "amount_not_positive",
    "exceeds_available",
    "exceeds_rolling_limit",
    "administrator_email_unverified",
];
const REFUSAL_MESSAGES = {
    feature_disabled: "Grants are not switched on for this platform yet.",
    organization_not_approved: "This organization's claim has not been approved yet.",
    no_verified_bank_account: "Add and verify a bank account before requesting a grant.",
    bank_account_cooling_off: "This bank account was added or changed recently. Grants to a new account are held for a " +
        "short period as a fraud control; it will become available shortly.",
    sanctions_not_cleared: "This organization is being reviewed. Grants are paused until it is done.",
    sanctions_list_stale: "Grants are paused platform-wide: the sanctions list has not refreshed recently, so no " +
        "screening can be relied on. This is an operational fault, not a finding about this " +
        "organization.",
    payouts_halted: "Grants to this organization are paused. Contact support — the destination account may need " +
        "to be verified again.",
    organization_too_new: "New organizations become eligible for grants after an initial period.",
    amount_not_positive: "Enter an amount greater than zero.",
    // "available to grant" was retired here on 2026-08-28: outside counsel read it as sounding
    // like an account payable belonging to the organization, which is the characterization the
    // whole design exists to avoid. The figure is the same; what it is called is not.
    exceeds_available: "That is more than the designated support currently held for this organization.",
    exceeds_rolling_limit: "That exceeds the daily grant limit. A reviewer has to approve it.",
    administrator_email_unverified: "Before a grant can be sent, at least one of this organization's administrators has to " +
        "confirm their email address. Sign in to the console and follow the prompt — it takes a " +
        "moment, and nothing else is affected in the meantime.",
};
export class PayoutRefusedError extends DomainError {
    refusal;
    constructor(refusal) {
        super("forbidden", REFUSAL_MESSAGES[refusal], { details: { refusal } });
        this.refusal = refusal;
    }
}
/**
 * Whether this organization could be paid at all, before any amount is considered.
 *
 * Returns the *first* refusal in a deliberate order: the reasons a person can do nothing about
 * come first, so an administrator is not told to verify a bank account for an organization that is
 * sanctions-held. Ordering error messages is not decoration — it decides what somebody does next.
 */
export function payoutEligibility(input) {
    if (!input.featureEnabled)
        return "feature_disabled";
    if (input.payoutsHaltedAt !== null)
        return "payouts_halted";
    if (!input.sanctionsCleared)
        return "sanctions_not_cleared";
    if (!sanctionsListIsCurrent(input.sanctionsListPublishedAt, input.now))
        return "sanctions_list_stale";
    if (input.claimStatus !== "approved")
        return "organization_not_approved";
    // After the sanctions and claim gates and before the bank ones: this is a reachability problem,
    // not a finding about the organization, and the message says so. Ordering matters because only
    // the first refusal is shown, and "we are reviewing you" must never be displaced by "confirm
    // your email" — nor the reverse.
    if (!input.administratorEmailVerified)
        return "administrator_email_unverified";
    if (input.now.epochMs - input.organizationCreatedAt.epochMs < NEW_ORGANIZATION_HOLD_MS) {
        return "organization_too_new";
    }
    if (!input.bankAccount || input.bankAccount.verifiedAt === null)
        return "no_verified_bank_account";
    if (input.bankAccount.payableFrom === null ||
        input.bankAccount.payableFrom.epochMs > input.now.epochMs) {
        return "bank_account_cooling_off";
    }
    return null;
}
/**
 * Whether this *amount* may leave, given the balance and what has already left today.
 *
 * Split from eligibility because the two are checked at different moments under different locks:
 * eligibility can be answered from a read, the amount cannot be answered safely without the row
 * lock the service takes.
 */
export function payoutAmountRefusal(input) {
    if (isNegative(input.amount) || isZero(input.amount))
        return "amount_not_positive";
    if (input.amount.amountMinor > input.available.amountMinor)
        return "exceeds_available";
    if (input.rollingTotalMinor + input.amount.amountMinor > PAYOUT_ROLLING_LIMIT_MINOR) {
        return "exceeds_rolling_limit";
    }
    return null;
}
/** Above the threshold, an organization administrator's approval is not enough. */
export const requiresPlatformApproval = (amount) => amount.amountMinor > PAYOUT_REVIEW_THRESHOLD_MINOR;
/* --------------------------------------------------------------- ACH returns */
/**
 * What an ACH return code means for a *credit*, which is not what it means for a debit.
 *
 * `R01` on the way in is a donor with insufficient funds. `R01` on the way out is **our** problem:
 * the originating balance was underfunded, the destination is fine, and retrying after funding it
 * is the right move. `R02`–`R04` say the destination account is closed, invalid, or does not
 * exist, and retrying into the same hole produces a second return and a second fee. Those halt the
 * organization until the account is verified again.
 */
export const HALTING_RETURN_CODES = ["R02", "R03", "R04"];
export function dispositionForReturnCode(code) {
    const normalized = code.trim().toUpperCase();
    if (normalized === "R01")
        return "refund_and_retry";
    if (HALTING_RETURN_CODES.includes(normalized))
        return "halt_organization";
    return "investigate";
}
/* ------------------------------------------------------------- the entries */
/**
 * The payout is submitted to the rail.
 *
 * ```
 * Dr grants_paid            amount
 *    Cr payouts_pending          amount
 * ```
 *
 * **Posted at `submitted`, not at `requested`.** A request is an intention; it moves no money and
 * an organization whose balance dropped when somebody clicked a button that a reviewer then
 * rejected would be right to complain. The consequence is that the available balance does not
 * reserve against an open request, which is why `payoutAmountRefusal` is re-checked under a row
 * lock at submission rather than trusted from the request.
 */
export function payoutSubmittedTransaction(input) {
    return twoLegged(input, "grants_paid", "payouts_pending");
}
/**
 * The ACH credit settles: cash leaves the platform's bank account.
 *
 * ```
 * Dr payouts_pending   amount
 *    Cr platform_cash       amount
 * ```
 *
 * The expense is recognised at **submission**, by the entry above: `grants_paid` is debited when
 * the grant goes to the rail, and this pair then moves the cash. Before 2026-08-28 the submission
 * entry debited `organization_payable` — a liability the gift had created — and there was nowhere
 * correct to put an expense at all. Once a gift became revenue (DEC-028), the grant became an
 * expense of the Foundation and the leg fell into place.
 */
export function payoutSettledTransaction(input) {
    return twoLegged(input, "payouts_pending", "platform_cash");
}
function twoLegged(input, debit, credit) {
    if (isNegative(input.amount) || isZero(input.amount)) {
        throw new DomainError("invalid_request", "A payout must have a positive amount");
    }
    const draft = {
        transactionId: input.transactionId,
        organizationId: input.organizationId,
        currency: input.amount.currency,
        memo: input.memo,
        entries: [
            { account: debit, direction: "debit", amount: input.amount, memo: input.memo },
            { account: credit, direction: "credit", amount: input.amount, memo: input.memo },
        ],
    };
    assertBalanced(draft);
    return draft;
}
//# sourceMappingURL=payout.js.map